import { useMemo } from 'react';

import type { InstantSearchOptions, UiState } from 'instantsearch.js';
import { history as historyRouter } from 'instantsearch.js/routers';

import { getLocation } from '@change-corgi/core/window';
import { useNavigate } from '@change-corgi/core-react/router';

import type { CustomRouteSearchState } from '#src/app/pages/search/shared/types';

import { windowTitle } from './config';
import { useStateMapping } from './hooks/useStateMapping';

type Result = ModelHookResult<{
	routing: InstantSearchOptions['routing'];
}>;

// Custom router hook.
// This hook allows us to modify the query parameters and synchronize them with the corgi router.
// stateMapping: Synchronizes the URL with the search state.
// router: Synchronizes the corgi router with the internal router.
//
// for more information, see the documentation:
// https://www.algolia.com/doc/api-reference/widgets/history-router/react/
export function useRouting(): Result {
	const {
		actions: { stateToRoute, routeToState },
	} = useStateMapping();
	const navigate = useNavigate();

	return {
		data: {
			routing: useMemo(
				() => ({
					router: historyRouter({
						getLocation,
						windowTitle: (routeState) => windowTitle(routeState as CustomRouteSearchState),
						push: (url) => {
							navigate(url, { replace: false });
						},
						parseURL: ({ qsModule, location }) => {
							const parsed = qsModule.parse(location.search, { ignoreQueryPrefix: true });
							return parsed as UiState;
						},
						createURL({ qsModule, location, routeState }) {
							const { pathname, hash, search } = location;
							const queryParameters = qsModule.parse(search, { ignoreQueryPrefix: true });

							if (Object.keys(routeState).length) {
								Object.assign(queryParameters, routeState);
							}

							let queryString = qsModule.stringify(queryParameters);

							if (queryString.length) {
								queryString = `?${queryString}`;
							}

							return `${pathname}${queryString}${hash}`;
						},
						// This option controls whether the URL is cleaned up from active refinements when the router is disposed of.
						// It’s useful when the search experience is in a modal, to remove query parameters from the URL when the modal is closed.
						// https://www.algolia.com/doc/api-reference/widgets/history-router/js/#widget-param-cleanurlondispose
						cleanUrlOnDispose: false,
					}),
					stateMapping: {
						stateToRoute,
						routeToState,
					},
				}),
				[navigate, routeToState, stateToRoute],
			),
		},
	};
}
